// import actions
import { DisplayCheckbox, DisplayError } from "../../actions/notifications.actions";
import { MergeStateChange, StateChange } from "../../actions/page.actions";
import {
	AddGroupToSelection,
	AddUserToSelection,
	RemoveUserFromSelection,
	ToggleUserSelection,
	ToggleUserSelectionSpinner,
	UpdateUserCount,
} from "../../actions/selection.actions";
import { RedoLastSearch, SearchUsers } from "../../actions/users.actions";

export function Search(
	{ query = "", fieldIds = [], letter = "", page = 1, limitSelected = false, lastName = "", ...additionalFilters },
	selectionObjectId,
	exclusionList,
	usersToExcludeSelectionId,
	excludeCurrentUser,
	storeName,
	signalToken
) {
	return (dispatch) => {
		/*
            Searching first passes through this function. We will do all the
            cleanup here and sync up the page state.

            The search object looks like this:
            {
                query           : string
                letter          : string
                page            : number
                limitSelected   : bool
                fieldIds        : array
            }
        */

		/*
            Sync the page state. But don't update the search input because
            it's debounced so updating it on search will have a delay.
        */
		const updatedPageState = {
			letter,
			lastName,
			limitSelected,
			page: 1,
		};

		dispatch(MergeStateChange(storeName, [], updatedPageState));

		/*
            Generate the search object.
        */
		const cleanedSearchObject = {
			query,
			fieldIds,
			letter,
			page,
			limitSelected,
			lastName,
			...additionalFilters,
		};

		dispatch(
			SearchUsers(
				cleanedSearchObject,
				selectionObjectId,
				exclusionList,
				usersToExcludeSelectionId,
				excludeCurrentUser,
				signalToken
			)
		);
	};
}

export function MergeSearch(
	searchObject,
	additionalFilters,
	searchFields,
	selectionObjectId,
	exclusionList,
	usersToExcludeSelectionId,
	excludeCurrentUser,
	storeName,
	signalToken
) {
	return (dispatch, getState) => {
		/*
            This is similar to search but it grabs the current values from
            the state if it isn't passed to the search object.
        */

		// get the state
		const currentState = getState();
		const pageState = currentState.page.getIn([storeName]);

		let cleanedSearchObject = {};
		const keyNames = [
			{ key: "page", store: "page" },
			{ key: "letter", store: "letter" },
			{ key: "query", store: "searchInput" },
			{ key: "lastName", store: "lastName" },
			{ key: "limitSelected", store: "limitSelected" },
		];

		cleanedSearchObject.fieldIds = searchFields;

		keyNames.forEach((ob) => {
			const { key, store } = ob;

			if (searchObject[key] === undefined) {
				cleanedSearchObject[key] = pageState.get(store);
			} else {
				cleanedSearchObject[key] = searchObject[key];
			}
		});

		// add the additional filters to the search object
		cleanedSearchObject = {
			...cleanedSearchObject,
			...additionalFilters.toJS(),
		};

		dispatch(
			Search(
				cleanedSearchObject,
				selectionObjectId,
				exclusionList.toArray(),
				usersToExcludeSelectionId,
				excludeCurrentUser,
				storeName,
				signalToken
			)
		);
	};
}

export function AddGroup(selectionObjectId, groupIds, exclusionList, filters, storeName) {
	return (dispatch) => {
		/*
            Local action to add group to the selection.

            On a successful response, the group curtain will close.
        */
		dispatch(StateChange(storeName, ["showIsAdding"], true));

		const callback = (res) => {
			if (res.status === 2) {
				dispatch(DisplayCheckbox());
				dispatch(RedoLastSearch());
				dispatch(UpdateUserCount(selectionObjectId));
			} else {
				dispatch(DisplayError("Unable to add group."));
			}
			dispatch(StateChange(storeName, ["showGroupCurtain"], false));
			dispatch(StateChange(storeName, ["showIsAdding"], false));
		};

		dispatch(AddGroupToSelection(selectionObjectId, groupIds, exclusionList.toArray(), filters, callback));
	};
}

export function AddUser(selectionObjectId, userIds, exclusionList) {
	return (dispatch) => {
		/*
            Local action to add a person to the selection.
            The exclusion list ensures that person doesn't already exist in
            a linked selection object
        */

		// trigger a spinner
		dispatch(ToggleUserSelectionSpinner(selectionObjectId, userIds));

		const callback = (res) => {
			if (res.status === 2) {
				dispatch(ToggleUserSelection(selectionObjectId, userIds));
				dispatch(UpdateUserCount(selectionObjectId));
			}
		};

		dispatch(AddUserToSelection(selectionObjectId, userIds, exclusionList.toArray(), callback));
	};
}

export function RemoveUser(selectionObjectId, userIds) {
	return (dispatch) => {
		/*
            Local action to add a person to the selection.
        */

		// trigger a spinner
		dispatch(ToggleUserSelectionSpinner(selectionObjectId, userIds));

		const callback = (res) => {
			if (res.status === 2) {
				dispatch(ToggleUserSelection(selectionObjectId, userIds));
				dispatch(UpdateUserCount(selectionObjectId));
			}
		};

		dispatch(RemoveUserFromSelection(selectionObjectId, userIds, callback));
	};
}
